import { call, put, all, spawn, takeLatest } from 'redux-saga/effects';

import request from 'utils/request';
import { AUDIT_HEADER } from 'utils/constants';

import {
  UPSERT_MERCHANT_REG_DETAILS_URL,
  DOWNLOAD_DOCUMENT_URL,
  UPLOAD_DOCS_URL,
} from 'constants/url';

import { showToast } from '../Toast/actions';

import {
  handlePaymentMethodsResponse,
  disableLoaderAction,
  savePaytmDataAction,
  savePaytmTestConfigAction,
  savePaytmProdConfigAction,
  handlePaypalConfigResponse,
  handleDeactivatePaypalCurrency,
  handleActivatePaypalCurrency,
  handleUpdateDefaultPaypalCurrency,
  handleEnablePaymentMethods,
} from './actions';
import {
  GET_DOCS_PER_PAYMENT_METHOD,
  GET_DOCS_PER_PAYMENT_METHOD_URL,
  SAVE_UPLOAD_DOCUMENTS_FORM,
  ENABLE_PAYMENT_METHOD,
  ENABLE_PAYMENT_METHOD_URL,
  DOWNLOAD_DOCUMENT,
  GET_PAYMENT_METHODS_URL,
  GET_PAYMENT_METHODS,
  GET_PAYTM_PROD_CONFIG_URL,
  GET_PAYTM_TEST_CONFIG_URL,
  GET_PAYTM_CHECKSUMHASH_URL,
  ADD_PAYTM_PROD_CONFIG_URL,
  UPDATE_PAYTM_TEST_CONFIG_URL,
  ADD_PAYTM_TEST_CONFIG_URL,
  RESET_PAYTM_WALLET_URL,
  GET_PAYTM_DATA,
  GET_PAYTM_TEST_CONFIG,
  GET_PAYTM_CHECKSUMHASH,
  ADD_PAYTM_PROD_CONFIG,
  ADD_PAYTM_TEST_CONFIG,
  RESET_PAYTM_WALLET,
  GET_PAYPAL_CONFIG,
  GET_PAYPAL_CONFIG_URL,
  GET_ACTIVE_PAYPAL_CURRENCIES_URL,
  GET_PAYPAL_CURRENCIES_URL,
  GET_DEFAULT_PAYPAL_CURRENCY_URL,
  INITIATE_PAYPAL,
  UPDATE_DEFAULT_PAYPAL_CURRENCY,
  ACTIVATE_PAYPAL_CURRENCY,
  DEACTIVATE_PAYPAL_CURRENCY,
  SAVE_PAYPAL_DATA,
  SAVE_PAYPAL_DATA_URL,
  DEACTIVATE_PAYPAL_CURRENCY_URL,
  RESET_PAYPAL_CONFIG,
  RESET_PAYPAL_CONFIG_URL,
  ACTIVATE_PAYPAL_CURRENCY_URL,
  UPDATE_DEFAULT_PAYPAL_CURRENCY_URL,
  INITIATE_PAYPAL_URL,
  ACCESS_COD_HANDELING_CHARGE,
  ACCESS_AMOUNT_AND_PIN_CODES_URL,
  ACCESS_COD_HANDELING_CHARGES,
  ACCESS_PIN_CODE_AMOUNT,
  UPDATE_PAYPAL_CONFIG,
  UPDATE_PAYPAL_CONFIG_URL,
} from './constants';
import { isActivatedInstantly } from './components/utils';
import { getFormattedCodDataForAmount } from './helpers';

const SETTINGS_PAYMENT_METHODS_AUDIT = 'settings-payment-methods';

export function* enablePaymentMethod({
  data: { successCb, errorCb, isInstantActive, ...data },
}) {
  try {
    const { url, method } = ENABLE_PAYMENT_METHOD_URL;
    const response = yield call(request, url, {
      method,
      body: JSON.stringify(data),
      headers: { [AUDIT_HEADER]: SETTINGS_PAYMENT_METHODS_AUDIT },
    });
    yield put(handleEnablePaymentMethods(response));
    // checking for status here
    if (isInstantActive) {
      const [code] = data.paymentMethods;
      const result = isActivatedInstantly(code, response);
      if (result) successCb();
      else
        errorCb({
          message:
            'Payment Method cannot be activated instantly due to api failure',
        });
    } else {
      successCb();
    }
  } catch (err) {
    errorCb(err);
    if (!isInstantActive) yield put(showToast(err));
  } finally {
    yield put(disableLoaderAction());
  }
}

export function* getDocsPerPaymentMethod({ data: { data, cb } }) {
  try {
    const { url, method } = GET_DOCS_PER_PAYMENT_METHOD_URL;
    const options = {
      headers: {
        'Content-type': 'application/json',
      },
      method,
      body: JSON.stringify(data),
    };
    const response = yield call(request, url, options);
    if (cb) cb(response);
  } catch (e) {
    yield put(showToast(e));
  } finally {
    yield put(disableLoaderAction());
  }
}

export function getPromiseArrayForUploadDocs(files) {
  const promiseArr = [];
  if (files) {
    const { url, method } = UPLOAD_DOCS_URL;
    files.forEach(obj => {
      const { docType, file } = obj;
      const formData = new FormData();
      formData.append('file', file, file.name);
      formData.append('docType', docType);
      promiseArr.push(
        // eslint-disable-next-line redux-saga/yield-effects
        call(request, url, { method, body: formData }),
      );
    });
  }
  return promiseArr;
}

export function* saveUploadDocumentsForm({
  data: {
    data: { files, regDetails, showError },
    successCb,
    errorCb,
  },
}) {
  try {
    const promiseArr = getPromiseArrayForUploadDocs(files);
    if (regDetails && regDetails.length > 0) {
      const { url, method } = UPSERT_MERCHANT_REG_DETAILS_URL;
      promiseArr.push(
        // eslint-disable-next-line redux-saga/yield-effects
        call(request, url, { method, body: JSON.stringify({ regDetails }) }),
      );
    }
    yield all(promiseArr);
    // once the documents and input fields are saved make the enable payment methods call
    // const { url, method } = ENABLE_PAYMENT_METHOD_URL;
    // yield call(request, url, {
    //   method,
    //   body: JSON.stringify({ paymentModeCode }),
    // });

    successCb();
  } catch (e) {
    showError(e);
    errorCb(e);
    yield put(showToast(e));
  }
}

export function* downloadDocuments({ data: { data, successCb } }) {
  try {
    const { url, method } = DOWNLOAD_DOCUMENT_URL;
    const options = {
      headers: {
        'Content-type': 'application/json',
      },
      method,
      queryString: data,
    };
    const response = yield call(request, url, options);
    successCb(response);
  } catch (e) {
    yield put(showToast(e));
  }
}

export function* getPaymentMethods() {
  try {
    const { url, method } = GET_PAYMENT_METHODS_URL;
    const response = yield call(request, url, { method });
    yield put(handlePaymentMethodsResponse(response));
  } catch (err) {
    yield put(showToast(new Error('Failed to load payment methods')));
  } finally {
    yield put(disableLoaderAction());
  }
}

export function* getPaytmData() {
  try {
    const promiseArr = [
      GET_PAYTM_PROD_CONFIG_URL,
      GET_PAYTM_TEST_CONFIG_URL,
      // eslint-disable-next-line redux-saga/yield-effects
    ].map(obj => call(request, obj.url, { method: obj.method }));

    const [paytmProdConfig, paytmTestConfig] = yield all(promiseArr);

    yield put(savePaytmDataAction({ paytmProdConfig, paytmTestConfig }));
  } catch (err) {
    yield put(showToast(err));
  } finally {
    yield put(disableLoaderAction());
  }
}

export function* getPaytmTestConfig() {
  try {
    const { url, method } = GET_PAYTM_TEST_CONFIG_URL;
    const response = yield call(request, url, { method });
    yield put(savePaytmTestConfigAction(response));
  } catch (err) {
    yield put(showToast(err));
  }
}

export function* getPaytmCheckSumHash({ data }) {
  const { paytmParams, merchantPaytmKey, cb } = data;
  try {
    const { url, method } = GET_PAYTM_CHECKSUMHASH_URL;
    const payload = {
      merchantPaytmId: paytmParams.MID,
      merchantPaytmWebsiteUrl: paytmParams.WEBSITE,
      merchantPaytmChannelId: paytmParams.CHANNEL_ID,
      merchantPaytmIndustryType: paytmParams.INDUSTRY_TYPE_ID,
      paytmOrderId: paytmParams.ORDER_ID,
      customerId: paytmParams.CUST_ID,
      txnAmount: paytmParams.TXN_AMOUNT,
      callBackUrl: paytmParams.CALLBACK_URL,
      email: paytmParams.EMAIL,
      merchantPaytmKey,
    };
    const options = {
      method,
      body: JSON.stringify(payload),
    };
    const response = yield call(request, url, options);
    cb({ ...paytmParams, CHECKSUMHASH: response });
  } catch (err) {
    yield put(showToast(err));
  }
}

export function* addPaytmProdConfig({ data }) {
  const { payload, actions, cb } = data;
  try {
    const { url, method } = ADD_PAYTM_PROD_CONFIG_URL;
    const options = {
      method,
      body: JSON.stringify(payload),
      headers: { [AUDIT_HEADER]: SETTINGS_PAYMENT_METHODS_AUDIT },
    };
    const response = yield call(request, url, options);
    yield put(savePaytmProdConfigAction(response));
    actions.setSubmitting(false);
    cb(false);
  } catch (err) {
    actions.setSubmitting(false);
    yield put(showToast(err));
  }
}

export function* addPaytmTestConfig({ data }) {
  const { payload, actions, isEditMode, cbShowForm, cbEditMode } = data;
  try {
    const { url, method } = isEditMode
      ? UPDATE_PAYTM_TEST_CONFIG_URL
      : ADD_PAYTM_TEST_CONFIG_URL;
    const options = {
      method,
      body: JSON.stringify(payload),
    };
    const response = yield call(request, url, options);
    yield put(savePaytmTestConfigAction(response));
    actions.setSubmitting(false);
    cbShowForm(false);
    if (cbEditMode) cbEditMode(false);
  } catch (err) {
    actions.setSubmitting(false);
    yield put(showToast(err));
  }
}

export function* resetPaytmWallet({ data }) {
  const { successCb, errorCb } = data;
  try {
    const { url, method } = RESET_PAYTM_WALLET_URL;
    yield call(request, url, {
      method,
      headers: { [AUDIT_HEADER]: SETTINGS_PAYMENT_METHODS_AUDIT },
    });
    successCb();
    yield put(
      showToast({
        message: 'PayTM Deactivated Successfully',
        type: 'SUCCESS',
      }),
    );
  } catch (err) {
    errorCb();
    yield put(showToast(err));
  } finally {
    yield put(disableLoaderAction());
  }
}

export function* getPaypalConfig() {
  try {
    const { url, method } = GET_PAYPAL_CONFIG_URL;
    const finalUrl = `${url}?count=1&version=v2&active=true`;
    const { content = [] } = yield call(request, finalUrl, { method });
    const activeAccount = content.find(obj => !!obj.active);
    const result = { activeAccount };

    if (activeAccount) {
      const promiseArr = [
        GET_ACTIVE_PAYPAL_CURRENCIES_URL,
        GET_PAYPAL_CURRENCIES_URL,
        GET_DEFAULT_PAYPAL_CURRENCY_URL,
        // eslint-disable-next-line redux-saga/yield-effects
      ].map(obj => call(request, obj.url, { method: obj.method }));

      const [activeCurrencies, paypalCurrencies, defaultCurrency] = yield all(
        promiseArr,
      );

      result.activeCurrencies = activeCurrencies;
      result.paypalCurrencies = paypalCurrencies;
      result.defaultCurrency = defaultCurrency;
    }
    yield put(handlePaypalConfigResponse(result));
  } catch (err) {
    yield put(showToast(err));
  } finally {
    yield put(disableLoaderAction());
  }
}

export function* initiatePaypal({ data: { cb } }) {
  try {
    const { url: requestUrl, method } = INITIATE_PAYPAL_URL;
    const { url } = yield call(request, requestUrl, { method });
    cb(url);
  } catch (err) {
    yield put(showToast(err));
  } finally {
    yield put(disableLoaderAction());
  }
}

export function* updateDefaultPaypalCurrency({
  data: { successCb, errorCb, currencyCode },
}) {
  try {
    const { url, method } = UPDATE_DEFAULT_PAYPAL_CURRENCY_URL;
    const finalUrl = url.replace('$currency_code$', currencyCode);
    const response = yield call(request, finalUrl, { method });
    if (successCb) successCb();
    yield put(handleUpdateDefaultPaypalCurrency(response));
  } catch (err) {
    if (errorCb) errorCb();
    yield put(disableLoaderAction());
    yield put(showToast(err));
  }
}

export function* activatePaypalCurrency({
  data: { successCb, currencyCode, errorCb },
}) {
  try {
    const { url, method } = ACTIVATE_PAYPAL_CURRENCY_URL;
    const finalUrl = url.replace('$currency_code$', currencyCode);
    const response = yield call(request, finalUrl, { method });
    if (successCb) successCb();
    yield put(handleActivatePaypalCurrency(response));
  } catch (err) {
    if (errorCb) errorCb();
    yield put(disableLoaderAction());
    yield put(showToast(err));
  }
}

export function* deactivatePaypalCurrency({ data: { cb, currencyCode } }) {
  try {
    const { url, method } = DEACTIVATE_PAYPAL_CURRENCY_URL;
    const finalUrl = url.replace('$currency_code$', currencyCode);
    const response = yield call(request, finalUrl, { method });
    if (cb) cb();
    yield put(handleDeactivatePaypalCurrency(response));
  } catch (err) {
    yield put(disableLoaderAction());
    yield put(showToast(err));
  }
}

export function* savePaypalData({ data: { data, successCb, errorCb } }) {
  try {
    const { url, method } = SAVE_PAYPAL_DATA_URL;
    const response = yield call(request, url, {
      method,
      body: JSON.stringify(data),
      headers: { [AUDIT_HEADER]: SETTINGS_PAYMENT_METHODS_AUDIT },
    });
    successCb(response);
  } catch (err) {
    errorCb();
    yield put(showToast(err));
  } finally {
    yield put(disableLoaderAction());
  }
}

export function* resetPaypalConfig({ data: { successCb, errorCb } }) {
  try {
    const { url, method } = RESET_PAYPAL_CONFIG_URL;
    yield call(request, url, {
      method,
      headers: { [AUDIT_HEADER]: SETTINGS_PAYMENT_METHODS_AUDIT },
    });
    successCb();
    yield put(
      showToast({
        message: 'Paypal Deactivated Successfully',
        type: 'SUCCESS',
      }),
    );
  } catch (err) {
    errorCb();
    yield put(showToast(err));
  } finally {
    yield put(disableLoaderAction());
  }
}

/* eslint-disable no-param-reassign */
/* eslint-disable indent */
export function* accessCodHadelingCharges({ data }) {
  const { successCb, errorCb, type, reqData } = data;
  try {
    const { url } = ACCESS_COD_HANDELING_CHARGE;
    const options = {
      method: type,
      ...(type === 'GET'
        ? { queryString: reqData }
        : {
            body: JSON.stringify(reqData),
          }),
    };

    const res = yield call(request, url, options);
    successCb(res);
  } catch (error) {
    if (error?.errorMeta?.statusCode === 400) {
      errorCb({ COD_HANDLING_CHARGES: '' });
    } else {
      yield put(showToast(error));
    }
  } finally {
    yield put(disableLoaderAction());
  }
}

export function* accessPinCodesAndAmount({
  data: { successCb, errorCb, type, reqData },
}) {
  try {
    const { url } = ACCESS_AMOUNT_AND_PIN_CODES_URL;
    if (type === 'GET') {
      const options = {
        method: type,
        queryString: reqData,
      };
      const res = yield call(request, url, options);
      successCb(res);
    } else {
      const promiseArr = Object.entries(reqData).map(reqObj => {
        const obj = getFormattedCodDataForAmount(reqObj);
        const options = {
          headers: {
            'Content-type': 'application/json',
          },
          method: type,
          body: JSON.stringify({
            ruleCategory: 'MERCHANT_SPECIFIC',
            rule: obj[0],
            configValue: Array.isArray(obj[1]) ? obj[1] : parseInt(obj[1], 10),
            active: obj[2],
          }),

          connectionTimeout: 60,
        };
        // eslint-disable-next-line
        return call(request, url, options);
      });
      const response = yield all(promiseArr);
      successCb(response);
    }
  } catch (error) {
    if (error?.errorMeta?.statusCode === 400) {
      errorCb({});
    } else {
      yield put(showToast(error));
    }
  }
}

export function* updatePaypalConfig({ data: { data, cb } }) {
  try {
    const { url, method } = UPDATE_PAYPAL_CONFIG_URL;
    yield call(request, url, {
      method,
      headers: { [AUDIT_HEADER]: SETTINGS_PAYMENT_METHODS_AUDIT },
      body: JSON.stringify(data),
    });
    if (cb) cb();
  } catch (e) {
    yield put(showToast(e));
  } finally {
    yield put(disableLoaderAction());
  }
}

export function* getPaymentMethodsSaga() {
  yield takeLatest(GET_PAYMENT_METHODS, getPaymentMethods);
}

export function* getDocsPerPaymentMethodSaga() {
  yield takeLatest(GET_DOCS_PER_PAYMENT_METHOD, getDocsPerPaymentMethod);
}

export function* saveUploadDocumentsFormSaga() {
  yield takeLatest(SAVE_UPLOAD_DOCUMENTS_FORM, saveUploadDocumentsForm);
}

export function* downloadDocumentsSaga() {
  yield takeLatest(DOWNLOAD_DOCUMENT, downloadDocuments);
}

export function* enablePaymentMethodSaga() {
  yield takeLatest(ENABLE_PAYMENT_METHOD, enablePaymentMethod);
}

export function* getPaytmDataSaga() {
  yield takeLatest(GET_PAYTM_DATA, getPaytmData);
}

export function* getPaytmTestConfigSaga() {
  yield takeLatest(GET_PAYTM_TEST_CONFIG, getPaytmTestConfig);
}

export function* getPaytmCheckSumHashSaga() {
  yield takeLatest(GET_PAYTM_CHECKSUMHASH, getPaytmCheckSumHash);
}

export function* addPaytmProdConfigSaga() {
  yield takeLatest(ADD_PAYTM_PROD_CONFIG, addPaytmProdConfig);
}

export function* addPaytmTestConfigSaga() {
  yield takeLatest(ADD_PAYTM_TEST_CONFIG, addPaytmTestConfig);
}

export function* resetPaytmWalletSaga() {
  yield takeLatest(RESET_PAYTM_WALLET, resetPaytmWallet);
}

export function* getPaypalConfigSaga() {
  yield takeLatest(GET_PAYPAL_CONFIG, getPaypalConfig);
}

export function* initiatePaypalSaga() {
  yield takeLatest(INITIATE_PAYPAL, initiatePaypal);
}

export function* updateDefaultPaypalCurrencySaga() {
  yield takeLatest(UPDATE_DEFAULT_PAYPAL_CURRENCY, updateDefaultPaypalCurrency);
}

export function* activatePaypalCurrencySaga() {
  yield takeLatest(ACTIVATE_PAYPAL_CURRENCY, activatePaypalCurrency);
}

export function* deactivatePaypalCurrencySaga() {
  yield takeLatest(DEACTIVATE_PAYPAL_CURRENCY, deactivatePaypalCurrency);
}

export function* savePaypalDataSaga() {
  yield takeLatest(SAVE_PAYPAL_DATA, savePaypalData);
}

export function* resetPaypalConfigSaga() {
  yield takeLatest(RESET_PAYPAL_CONFIG, resetPaypalConfig);
}

export function* accessCodHadelingChargesSaga() {
  yield takeLatest(ACCESS_COD_HANDELING_CHARGES, accessCodHadelingCharges);
}

export function* accessPinCodesAndAmountSaga() {
  yield takeLatest(ACCESS_PIN_CODE_AMOUNT, accessPinCodesAndAmount);
}

export function* updatePaypalConfigSaga() {
  yield takeLatest(UPDATE_PAYPAL_CONFIG, updatePaypalConfig);
}

// Individual exports for testing
export default function* paymentMethodsV2Saga() {
  // See example in containers/HomePage/saga.js

  const sagas = [
    getPaymentMethodsSaga,
    getDocsPerPaymentMethodSaga,
    saveUploadDocumentsFormSaga,
    downloadDocumentsSaga,
    enablePaymentMethodSaga,
    getPaytmDataSaga,
    getPaytmTestConfigSaga,
    getPaytmCheckSumHashSaga,
    addPaytmProdConfigSaga,
    addPaytmTestConfigSaga,
    resetPaytmWalletSaga,
    getPaypalConfigSaga,
    initiatePaypalSaga,
    updateDefaultPaypalCurrencySaga,
    activatePaypalCurrencySaga,
    deactivatePaypalCurrencySaga,
    savePaypalDataSaga,
    resetPaypalConfigSaga,
    accessCodHadelingChargesSaga,
    accessPinCodesAndAmountSaga,
    updatePaypalConfigSaga,
  ];

  yield all(
    sagas.map(saga =>
      // eslint-disable-next-line func-names
      spawn(function*() {
        while (true) {
          try {
            yield call(saga);
            break;
          } catch (e) {
            // eslint-disable-next-line no-console
            console.log(e);
          }
        }
      }),
    ),
  );
}
